import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from './config';
import JsonEditor from './components/JsonEditor';
import useOAuthConnect from './hooks/useOAuthConnect';

const containerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg)',
  color: 'var(--text)',
  padding: '20px',
  boxSizing: 'border-box',
};

const contentStyle = {
  maxWidth: '800px',
  margin: '0 auto',
  width: '100%',
  background: 'var(--panel)',
  padding: '24px',
  borderRadius: '10px',
  border: '1px solid var(--border)',
  boxShadow: 'var(--elev-1)',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
};

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  marginBottom: '15px',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  fontSize: '13px',
  background: 'var(--bg)',
  color: 'var(--text)',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: 500,
  color: 'var(--muted)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const buttonStyle = {
  padding: '7px 14px',
  backgroundColor: 'var(--accent)',
  color: 'var(--accent-fg)',
  border: '1px solid var(--accent)',
  borderRadius: '6px',
  cursor: 'pointer',
  marginRight: '10px',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.2,
};

const deleteButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--error)',
  borderColor: 'var(--error)',
  color: '#fff',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--border)',
};


// Maps OAuth provider name → service_type used in credentials
const OAUTH_SERVICE_TYPE_MAP = {
  google: 'google_account',
  microsoft: 'microsoft',
  slack: 'slack',
  github: 'github',
  notion: 'notion',
  hubspot: 'hubspot',
  linear: 'linear',
};
// Reverse map: service_type → OAuth provider name
const SERVICE_TYPE_OAUTH_MAP = Object.fromEntries(
  Object.entries(OAUTH_SERVICE_TYPE_MAP).map(([p, s]) => [s, p])
);

export default function CredentialManager({ onBack, initialServiceType, initialCredentialId }) {
  const [credentials, setCredentials] = useState([]);
  const [selectedCredentialIds, setSelectedCredentialIds] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [currentCredentialId, setCurrentCredentialId] = useState(null);
  const [editingOAuthManaged, setEditingOAuthManaged] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  // formData.data is now an object, not a string
  const [formData, setFormData] = useState({
    name: '',
    service_type: 'postgres',
    data: {}
  });
  // OAuth: shared hook for popup connect flow
  const handleOAuthSuccess = useCallback((credentialId) => {
    fetchCredentials();
    setIsAdding(false);
    setEditingOAuthManaged(false);
    setFormData({ name: '', service_type: 'postgres', data: {} });
  }, []);
  const handleOAuthError = useCallback((msg) => { setError(msg); }, []);
  const { connect: oauthConnect, connecting: oauthConnecting, oauthProviders: allOauthProviders } = useOAuthConnect({
    onSuccess: handleOAuthSuccess,
    onError: handleOAuthError,
  });
  // Filter to only available providers for the UI
  const oauthProviders = allOauthProviders.filter(p => p.available);

  const handleOAuthConnect = (provider, _serviceType, credentialId) => {
    const credName = formData.name || `My ${provider.charAt(0).toUpperCase() + provider.slice(1)} Account`;
    setError(null);
    oauthConnect(provider, credName, credentialId);
  };

  useEffect(() => {
    if (initialServiceType) {
      setFormData(prev => ({ ...prev, service_type: initialServiceType }));
      setIsAdding(true);
    }
  }, [initialServiceType]);
  const [error, setError] = useState(null);
  const [testStatus, setTestStatus] = useState('idle'); // idle, testing, success, error
  const [testMessage, setTestMessage] = useState('');
  const [serviceTypeSearch, setServiceTypeSearch] = useState('');
  const [serviceTypeOpen, setServiceTypeOpen] = useState(false);
  const serviceTypeDropdownRef = useRef(null);

  useEffect(() => {
    if (!serviceTypeOpen) return;
    const handleClickOutside = (e) => {
      if (serviceTypeDropdownRef.current && !serviceTypeDropdownRef.current.contains(e.target)) {
        setServiceTypeOpen(false);
        setServiceTypeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [serviceTypeOpen]);

  const serviceTypes = [
    { value: 'postgres', label: 'PostgreSQL' },
    { value: 'mysql', label: 'MySQL' },
    { value: 'sqlserver', label: 'SQL Server' },
    { value: 'mongodb', label: 'MongoDB' },
    { value: 'google_account', label: 'Google Account (Sheets/Docs/Gmail)' },
    { value: 'microsoft', label: 'Microsoft 365 (Excel/Outlook/SharePoint)' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'gemini', label: 'Google Gemini' },
    { value: 'huggingface', label: 'Hugging Face' },
    { value: 'qdrant', label: 'Qdrant Vector DB' },
    { value: 'jira', label: 'Jira' },
    { value: 'confluence', label: 'Confluence' },
    { value: 'slack', label: 'Slack' },
    { value: 'notion', label: 'Notion' },
    { value: 'trello', label: 'Trello' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'smtp', label: 'SMTP (Email)' },
    { value: 'imap', label: 'IMAP (Email Inbox)' },
    { value: 'kafka', label: 'Kafka' },
    { value: 'aws_sqs', label: 'AWS SQS' },
    { value: 'rabbitmq', label: 'RabbitMQ' },
    { value: 'redis', label: 'Redis' },
    { value: 'object_storage', label: 'Object Storage (S3-Compatible)' },
    { value: 'azure_ai', label: 'Azure AI' },
    { value: 'google_cloud', label: 'Google Cloud' },
    { value: 'aws_s3', label: 'AWS S3' },
    { value: 'azure_blob', label: 'Azure Blob Storage' },
    { value: 'gcs', label: 'Google Cloud Storage' },
    { value: 'anthropic', label: 'Anthropic Claude' },
    { value: 'github', label: 'GitHub' },
    { value: 'google_calendar', label: 'Google Calendar' },
    { value: 'ssh', label: 'SSH' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'quickbooks', label: 'QuickBooks' },
    { value: 'xero', label: 'Xero' },
    { value: 'exchange_rate', label: 'Exchange Rate API' },
    { value: 'url_shortener', label: 'URL Shortener (Bitly)' },
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'twilio', label: 'Twilio' },
    { value: 'sendgrid', label: 'SendGrid' },
    { value: 'mailchimp', label: 'Mailchimp' },
    { value: 'twitter', label: 'Twitter / X' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'instagram', label: 'Instagram Business' },
    { value: 'convertkit', label: 'ConvertKit' },
    { value: 'zendesk', label: 'Zendesk' },
    { value: 'monday', label: 'Monday.com' },
    { value: 'linear', label: 'Linear' },
    { value: 'asana', label: 'Asana' },
    { value: 'clickup', label: 'ClickUp' },
    { value: 'shopify', label: 'Shopify' },
    { value: 'woocommerce', label: 'WooCommerce' },
    { value: 'ollama', label: 'Ollama (Local LLM)' },
    { value: 'sftp', label: 'SFTP / FTP' },
    { value: 'snowflake', label: 'Snowflake' },
    { value: 'bigquery', label: 'Google BigQuery' },
    { value: 'databricks', label: 'Databricks' },
    { value: 'pipedrive', label: 'Pipedrive' },
    { value: 'zoho_crm', label: 'Zoho CRM' },
    { value: 'dynamics365', label: 'Microsoft Dynamics 365' },
    { value: 'intercom', label: 'Intercom' },
    { value: 'freshdesk', label: 'Freshdesk' },
    { value: 'servicenow', label: 'ServiceNow' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'outlook_calendar', label: 'Outlook Calendar' },
    { value: 'pagerduty', label: 'PagerDuty' },
    { value: 'calendly', label: 'Calendly' },
    // §13.1 — Database & Storage
    { value: 'firebase', label: 'Firebase / Firestore' },
    { value: 'dynamodb', label: 'AWS DynamoDB' },
    { value: 'supabase', label: 'Supabase' },
    { value: 'airtable', label: 'Airtable' },
    { value: 'elasticsearch', label: 'Elasticsearch' },
    { value: 'neon', label: 'Neon / PlanetScale / Turso' },
    // §13.2 — AI
    { value: 'perplexity', label: 'Perplexity / Tavily (Web Search)' },
    // §13.4 — Developer & DevOps
    { value: 'gitlab', label: 'GitLab' },
    { value: 'docker', label: 'Docker Host' },
    { value: 'kubernetes', label: 'Kubernetes' },
    { value: 'groq', label: 'Groq (Llama / Mixtral)' },
    { value: 'mistral', label: 'Mistral AI' },
    // §4.90 — SOAR Pack
    { value: 'virustotal', label: 'VirusTotal' },
    { value: 'abuseipdb', label: 'AbuseIPDB' },
    { value: 'shodan', label: 'Shodan' },
    { value: 'greynoise', label: 'GreyNoise' },
    { value: 'splunk', label: 'Splunk' },
    { value: 'datadog_security', label: 'Datadog (Security Signals)' },
    { value: 'sentinel', label: 'Microsoft Sentinel' },
    { value: 'crowdstrike', label: 'CrowdStrike Falcon' },
    { value: 'okta', label: 'Okta' },
    { value: 'qradar', label: 'IBM QRadar' },
    { value: 'microsoft_defender', label: 'Microsoft Defender for Endpoint' },
    { value: 'microsoft_sentinel', label: 'Microsoft Sentinel' },
    { value: 'tanium', label: 'Tanium' },
    { value: 'sumologic', label: 'Sumo Logic' },
    { value: 'aws_kinesis', label: 'AWS Kinesis' },
  ];

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/credentials/`, { headers });
      if (!res.ok) throw new Error('Failed to fetch credentials');
      const data = await res.json();
      setCredentials(data);
      setSelectedCredentialIds(prev => prev.filter(id => data.some(credential => credential.id === id)));
      // Auto-open a specific credential passed from the command palette
      if (initialCredentialId && data.some(c => c.id === initialCredentialId)) {
        handleEdit(initialCredentialId);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!(await window.confirm('Are you sure you want to delete this credential?'))) return;
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(`${API_BASE_URL}/credentials/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete credential');
      fetchCredentials();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCredentialIds.length === 0) return;
    if (!(await window.confirm(`Are you sure you want to delete ${selectedCredentialIds.length} selected credentials?`))) return;
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_BASE_URL}/bulk/credentials/delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ credential_ids: selectedCredentialIds })
      });
      if (!res.ok) throw new Error('Failed to delete selected credentials');
      setSelectedCredentialIds([]);
      fetchCredentials();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleCredentialSelection = (credentialId) => {
    setSelectedCredentialIds(prev => prev.includes(credentialId) ? prev.filter(id => id !== credentialId) : [...prev, credentialId]);
  };

  const toggleSelectAllCredentials = () => {
    if (credentials.length === 0) {
      setSelectedCredentialIds([]);
      return;
    }
    const allSelected = credentials.every(credential => selectedCredentialIds.includes(credential.id));
    if (allSelected) {
      setSelectedCredentialIds([]);
    } else {
      setSelectedCredentialIds(credentials.map(credential => credential.id));
    }
  };

  const handleEdit = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(`${API_BASE_URL}/credentials/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch credential details');
      const data = await res.json();
      
      setFormData({
        name: data.name,
        service_type: data.service_type,
        data: data.data || {}
      });
      setEditingOAuthManaged(data.oauth_managed || false);
      setCurrentCredentialId(id);
      setIsAdding(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        service_type: formData.service_type,
        data: formData.data
      };

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let url = `${API_BASE_URL}/credentials/`;
      let method = 'POST';

      if (currentCredentialId) {
        url = `${API_BASE_URL}/credentials/${currentCredentialId}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to save credential');
      }

      setIsAdding(false);
      setCurrentCredentialId(null);
      setEditingOAuthManaged(false);
      setFormData({ name: '', service_type: 'postgres', data: {} });
      fetchCredentials();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTestConnection = async () => {
    setTestStatus('testing');
    setTestMessage('Testing connection...');
    try {
      const payload = {
        service_type: formData.service_type,
        data: formData.data
      };
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/credentials/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      
      const result = await res.json();
      if (result.success) {
        setTestStatus('success');
        setTestMessage(result.message || 'Connection successful!');
      } else {
        setTestStatus('error');
        setTestMessage(result.message || 'Connection failed.');
      }
    } catch (err) {
      setTestStatus('error');
      setTestMessage(err.message || 'Failed to connect to server.');
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value
      }
    }));
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    // Reset data when type changes
    setFormData({
      ...formData,
      service_type: type,
      data: {}
    });
    setTestStatus('idle');
    setTestMessage('');
  };

  const renderFields = () => {
    const type = formData.service_type;
    const data = formData.data;

    switch (type) {
      case 'postgres':
      case 'mysql':
      case 'sqlserver':
        return (
          <>
            <div>
              <label style={labelStyle}>Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="localhost" required />
            </div>
            <div>
              <label style={labelStyle}>Port</label>
              <input style={inputStyle} type="number" value={data.port || ''} onChange={e => handleFieldChange('port', parseInt(e.target.value))} placeholder="5432" required />
            </div>
            <div>
              <label style={labelStyle}>Database Name</label>
              <input style={inputStyle} type="text" value={data.dbname || ''} onChange={e => handleFieldChange('dbname', e.target.value)} placeholder="mydb" required />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.user || ''} onChange={e => handleFieldChange('user', e.target.value)} placeholder="user" required />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} placeholder="password" required />
            </div>
          </>
        );
      case 'mongodb':
        return (
          <>
            <div>
              <label style={labelStyle}>Connection String (optional)</label>
              <input style={inputStyle} type="text" value={data.connection_string || ''} onChange={e => handleFieldChange('connection_string', e.target.value)} placeholder="mongodb://user:pass@host:27017/mydb  or  mongodb+srv://..." />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '3px' }}>If provided, the fields below are ignored.</div>
            </div>
            <div>
              <label style={labelStyle}>Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="localhost" />
            </div>
            <div>
              <label style={labelStyle}>Port</label>
              <input style={inputStyle} type="number" value={data.port || ''} onChange={e => handleFieldChange('port', parseInt(e.target.value))} placeholder="27017" />
            </div>
            <div>
              <label style={labelStyle}>Database Name</label>
              <input style={inputStyle} type="text" value={data.dbname || ''} onChange={e => handleFieldChange('dbname', e.target.value)} placeholder="mydb" />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.user || ''} onChange={e => handleFieldChange('user', e.target.value)} placeholder="user" />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} placeholder="password" />
            </div>
          </>
        );
      case 'openai':
      case 'gemini':
      case 'huggingface':
      case 'notion':
      case 'azure_ai':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="sk-..." required />
          </div>
        );
      case 'kafka':
        return (
          <>
            <div>
              <label style={labelStyle}>Bootstrap Servers</label>
              <input style={inputStyle} type="text" value={data.bootstrap_servers || ''} onChange={e => handleFieldChange('bootstrap_servers', e.target.value)} placeholder="broker1:9092,broker2:9092" required />
            </div>
            <div>
              <label style={labelStyle}>Security Protocol</label>
              <select style={inputStyle} value={data.security_protocol || 'PLAINTEXT'} onChange={e => handleFieldChange('security_protocol', e.target.value)}>
                <option value="PLAINTEXT">PLAINTEXT</option>
                <option value="SSL">SSL (Mutual TLS — Aiven, Confluent)</option>
                <option value="SASL_PLAINTEXT">SASL_PLAINTEXT</option>
                <option value="SASL_SSL">SASL_SSL</option>
              </select>
            </div>
            {(data.security_protocol === 'SASL_PLAINTEXT' || data.security_protocol === 'SASL_SSL') && <>
              <div>
                <label style={labelStyle}>SASL Mechanism</label>
                <select style={inputStyle} value={data.sasl_mechanism || 'PLAIN'} onChange={e => handleFieldChange('sasl_mechanism', e.target.value)}>
                  <option value="PLAIN">PLAIN</option>
                  <option value="SCRAM-SHA-256">SCRAM-SHA-256</option>
                  <option value="SCRAM-SHA-512">SCRAM-SHA-512</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>SASL Username</label>
                <input style={inputStyle} type="text" value={data.sasl_username || ''} onChange={e => handleFieldChange('sasl_username', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>SASL Password</label>
                <input style={inputStyle} type="password" value={data.sasl_password || ''} onChange={e => handleFieldChange('sasl_password', e.target.value)} />
              </div>
            </>}
            {(data.security_protocol === 'SSL' || data.security_protocol === 'SASL_SSL') && <>
              <div>
                <label style={labelStyle}>CA Certificate (PEM)</label>
                <textarea style={{...inputStyle, height: '100px', fontFamily: 'monospace', fontSize: '11px'}} value={data.ssl_ca || ''} onChange={e => handleFieldChange('ssl_ca', e.target.value)} placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'} />
              </div>
              <div>
                <label style={labelStyle}>Client Certificate (PEM) — required for mutual TLS</label>
                <textarea style={{...inputStyle, height: '100px', fontFamily: 'monospace', fontSize: '11px'}} value={data.ssl_cert || ''} onChange={e => handleFieldChange('ssl_cert', e.target.value)} placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'} />
              </div>
              <div>
                <label style={labelStyle}>Client Key (PEM) — required for mutual TLS</label>
                <textarea style={{...inputStyle, height: '100px', fontFamily: 'monospace', fontSize: '11px'}} value={data.ssl_key || ''} onChange={e => handleFieldChange('ssl_key', e.target.value)} placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'} />
              </div>
              <div>
                <label style={labelStyle}>Key Password (Optional)</label>
                <input style={inputStyle} type="password" value={data.ssl_password || ''} onChange={e => handleFieldChange('ssl_password', e.target.value)} />
              </div>
            </>}
          </>
        );
      case 'redis':
        return (
          <>
            <div>
              <label style={labelStyle}>Redis URL (recommended for managed Redis)</label>
              <input style={inputStyle} type="text" value={data.redis_url || ''} onChange={e => handleFieldChange('redis_url', e.target.value)} placeholder="rediss://default:password@host:port/0" />
              <small style={{color: 'var(--text-secondary)', fontSize: '11px'}}>Use rediss:// for SSL (Aiven, Upstash, ElastiCache TLS). Leave blank to use Host/Port fields below.</small>
            </div>
            {!data.redis_url && <>
              <div>
                <label style={labelStyle}>Host</label>
                <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="localhost" />
              </div>
              <div>
                <label style={labelStyle}>Port</label>
                <input style={inputStyle} type="number" value={data.port || ''} onChange={e => handleFieldChange('port', parseInt(e.target.value))} placeholder="6379" />
              </div>
              <div>
                <label style={labelStyle}>Database Index (Optional)</label>
                <input style={inputStyle} type="number" value={data.db || ''} onChange={e => handleFieldChange('db', parseInt(e.target.value))} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Username (Optional)</label>
                <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Password (Optional)</label>
                <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px'}}>
                <input type="checkbox" id="redis_ssl" checked={!!data.use_ssl} onChange={e => handleFieldChange('use_ssl', e.target.checked)} />
                <label htmlFor="redis_ssl" style={{...labelStyle, margin: 0}}>Use SSL / TLS (rediss://)</label>
              </div>
            </>}
          </>
        );
      case 'rabbitmq':
        return (
          <>
            <div>
              <label style={labelStyle}>AMQP URL</label>
              <input style={inputStyle} type="text" value={data.amqp_url || ''} onChange={e => handleFieldChange('amqp_url', e.target.value)} placeholder="amqps://user:pass@host:5671/vhost" required />
            </div>
            <div>
              <label style={labelStyle}>Username (Optional)</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Password (Optional)</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} />
            </div>
          </>
        );
      case 'qdrant':
        return (
          <>
            <div>
              <label style={labelStyle}>Qdrant URL</label>
              <input style={inputStyle} type="text" value={data.qdrant_url || ''} onChange={e => handleFieldChange('qdrant_url', e.target.value)} placeholder="http://localhost:6333" required />
            </div>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.qdrant_api_key || ''} onChange={e => handleFieldChange('qdrant_api_key', e.target.value)} placeholder="API Key (optional)" />
            </div>
          </>
        );
      case 'excel':
      case 'microsoft':
        return (
          <>
            <div>
              <label style={labelStyle}>Client ID</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Tenant ID</label>
              <input style={inputStyle} type="text" value={data.tenant_id || ''} onChange={e => handleFieldChange('tenant_id', e.target.value)} required />
            </div>
          </>
        );
      case 'jira':
        return (
          <>
            <div>
              <label style={labelStyle}>Jira Domain</label>
              <input style={inputStyle} type="text" value={data.domain || ''} onChange={e => handleFieldChange('domain', e.target.value)} placeholder="your-domain.atlassian.net" required />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={data.email || ''} onChange={e => handleFieldChange('email', e.target.value)} placeholder="user@example.com" required />
            </div>
            <div>
              <label style={labelStyle}>API Token</label>
              <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="Atlassian API Token" required />
            </div>
          </>
        );
      case 'confluence':
        return (
          <>
            <div>
              <label style={labelStyle}>Confluence Domain</label>
              <input style={inputStyle} type="text" value={data.domain || ''} onChange={e => handleFieldChange('domain', e.target.value)} placeholder="your-domain.atlassian.net" required />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={data.email || ''} onChange={e => handleFieldChange('email', e.target.value)} placeholder="user@example.com" required />
            </div>
            <div>
              <label style={labelStyle}>API Token</label>
              <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="Atlassian API Token" required />
            </div>
          </>
        );
      case 'slack':
        return (
          <div>
            <label style={labelStyle}>Bot Token (xoxb-...)</label>
            <input style={inputStyle} type="password" value={data.bot_token || ''} onChange={e => handleFieldChange('bot_token', e.target.value)} placeholder="xoxb-..." required />
          </div>
        );
      case 'discord':
      case 'teams':
        return (
          <div>
            <label style={labelStyle}>Webhook URL</label>
            <input style={inputStyle} type="text" value={data.webhook_url || ''} onChange={e => handleFieldChange('webhook_url', e.target.value)} placeholder="https://..." required />
          </div>
        );
      case 'google_account':
        return (
          <div>
             <label style={labelStyle}>Service Account JSON</label>
             <div style={{ marginBottom: '10px', fontSize: '0.9em', color: 'var(--text-tertiary)' }}>
               Upload your Google Service Account JSON file or paste its content below.
             </div>
             <ServiceAccountInput 
                value={data} 
                onChange={(newData) => setFormData(prev => ({ ...prev, data: newData }))} 
                onError={(msg) => setError(msg)}
             />
          </div>
        );
      case 'trello':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Trello API Key" required />
            </div>
            <div>
              <label style={labelStyle}>Token</label>
              <input style={inputStyle} type="password" value={data.token || ''} onChange={e => handleFieldChange('token', e.target.value)} placeholder="Trello Server Token" required />
            </div>
          </>
        );
      case 'hubspot':
        return (
          <div>
            <label style={labelStyle}>Access Token</label>
            <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="pat-..." required />
          </div>
        );
      case 'sftp':
        return (
          <>
            <div>
              <label style={labelStyle}>Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="sftp.example.com" required />
            </div>
            <div>
              <label style={labelStyle}>Port</label>
              <input style={inputStyle} type="number" value={data.port || ''} onChange={e => handleFieldChange('port', e.target.value)} placeholder="22" />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} placeholder="Leave blank if using private key" />
            </div>
            <div>
              <label style={labelStyle}>Private Key (PEM, optional)</label>
              <textarea style={{...inputStyle, height: '80px', fontFamily: 'monospace', fontSize: '11px'}} value={data.private_key || ''} onChange={e => handleFieldChange('private_key', e.target.value)} placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..." />
            </div>
          </>
        );
      case 'snowflake':
        return (
          <>
            <div>
              <label style={labelStyle}>Account Identifier</label>
              <input style={inputStyle} type="text" value={data.account || ''} onChange={e => handleFieldChange('account', e.target.value)} placeholder="orgname-accountname" required />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.user || ''} onChange={e => handleFieldChange('user', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Warehouse (optional)</label>
              <input style={inputStyle} type="text" value={data.warehouse || ''} onChange={e => handleFieldChange('warehouse', e.target.value)} placeholder="COMPUTE_WH" />
            </div>
            <div>
              <label style={labelStyle}>Database (optional)</label>
              <input style={inputStyle} type="text" value={data.database || ''} onChange={e => handleFieldChange('database', e.target.value)} placeholder="MY_DATABASE" />
            </div>
            <div>
              <label style={labelStyle}>Schema (optional)</label>
              <input style={inputStyle} type="text" value={data.schema || ''} onChange={e => handleFieldChange('schema', e.target.value)} placeholder="PUBLIC" />
            </div>
            <div>
              <label style={labelStyle}>Role (optional)</label>
              <input style={inputStyle} type="text" value={data.role || ''} onChange={e => handleFieldChange('role', e.target.value)} placeholder="SYSADMIN" />
            </div>
          </>
        );
      case 'bigquery':
        return (
          <div>
            <label style={labelStyle}>Service Account JSON</label>
            <div style={{ marginBottom: '10px', fontSize: '0.9em', color: 'var(--text-tertiary)' }}>
              Upload your Google Cloud Service Account JSON key file or paste its content below.
            </div>
            <ServiceAccountInput
              value={data}
              onChange={(newData) => setFormData(prev => ({ ...prev, data: newData }))}
              onError={(msg) => setError(msg)}
            />
          </div>
        );
      case 'databricks':
        return (
          <>
            <div>
              <label style={labelStyle}>Workspace Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="https://adb-1234567890.1.azuredatabricks.net" required />
            </div>
            <div>
              <label style={labelStyle}>Personal Access Token</label>
              <input style={inputStyle} type="password" value={data.token || ''} onChange={e => handleFieldChange('token', e.target.value)} placeholder="dapi..." required />
            </div>
          </>
        );
      case 'pipedrive':
        return (
          <>
            <div>
              <label style={labelStyle}>API Token</label>
              <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="Your Pipedrive API token" required />
            </div>
            <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Find your API token in Pipedrive → Settings → Personal preferences → API.
            </div>
          </>
        );
      case 'zoho_crm':
        return (
          <>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="Zoho OAuth2 access token" required />
            </div>
            <div>
              <label style={labelStyle}>API Domain (optional)</label>
              <input style={inputStyle} type="text" value={data.api_domain || ''} onChange={e => handleFieldChange('api_domain', e.target.value)} placeholder="https://www.zohoapis.com" />
            </div>
            <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Generate an OAuth2 access token via Zoho API Console. Use api_domain for EU/IN data centers.
            </div>
          </>
        );
      case 'dynamics365':
        return (
          <>
            <div>
              <label style={labelStyle}>Tenant URL</label>
              <input style={inputStyle} type="text" value={data.tenant_url || ''} onChange={e => handleFieldChange('tenant_url', e.target.value)} placeholder="https://yourorg.crm.dynamics.com" required />
            </div>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="Azure AD OAuth2 access token" required />
            </div>
            <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Obtain an OAuth2 access token from Azure AD for your Dynamics 365 instance.
            </div>
          </>
        );
      case 'intercom':
        return (
          <>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="Your Intercom access token" required />
            </div>
            <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Find your token in Intercom → Settings → Integrations → Developer Hub → Your App → Authentication.
            </div>
          </>
        );
      case 'freshdesk':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your Freshdesk API key" required />
            </div>
            <div>
              <label style={labelStyle}>Domain</label>
              <input style={inputStyle} type="text" value={data.domain || ''} onChange={e => handleFieldChange('domain', e.target.value)} placeholder="yourcompany (without .freshdesk.com)" required />
            </div>
            <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Find your API key in Freshdesk → Profile Settings → API Key. Enter only your subdomain (e.g. "acme").
            </div>
          </>
        );
      case 'servicenow':
        return (
          <>
            <div>
              <label style={labelStyle}>Instance</label>
              <input style={inputStyle} type="text" value={data.instance || ''} onChange={e => handleFieldChange('instance', e.target.value)} placeholder="dev12345 (or full URL)" required />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} required />
            </div>
            <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Enter your instance name (e.g. "dev12345") or full URL. Use a service account with Table API access.
            </div>
          </>
        );
      case 'telegram':
        return (
          <div>
            <label style={labelStyle}>Bot Token</label>
            <input style={inputStyle} type="password" value={data.bot_token || ''} onChange={e => handleFieldChange('bot_token', e.target.value)} placeholder="123456789:ABCdefGHI..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Create a bot via @BotFather on Telegram and paste the token here.
            </div>
          </div>
        );
      case 'zoom':
        return (
          <>
            <div>
              <label style={labelStyle}>Account ID</label>
              <input style={inputStyle} type="text" value={data.account_id || ''} onChange={e => handleFieldChange('account_id', e.target.value)} placeholder="Your Zoom Account ID" required />
            </div>
            <div>
              <label style={labelStyle}>Client ID</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} placeholder="Server-to-Server OAuth Client ID" required />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} placeholder="Server-to-Server OAuth Client Secret" required />
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
              Create a Server-to-Server OAuth app in the Zoom App Marketplace to get these credentials.
            </div>
          </>
        );
      case 'outlook_calendar':
        return (
          <>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="eyJ..." />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Provide either an access token OR the three fields below (Client Credentials flow).
              </div>
            </div>
            <div>
              <label style={labelStyle}>Tenant ID</label>
              <input style={inputStyle} type="text" value={data.tenant_id || ''} onChange={e => handleFieldChange('tenant_id', e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </div>
            <div>
              <label style={labelStyle}>Client ID</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} placeholder="Azure AD App Client ID" />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} placeholder="Azure AD App Client Secret" />
            </div>
          </>
        );
      case 'pagerduty':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your PagerDuty API key" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Generate a REST API key in PagerDuty → Integrations → API Access Keys.
            </div>
          </div>
        );
      case 'calendly':
        return (
          <div>
            <label style={labelStyle}>Personal Access Token</label>
            <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="eyJra..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Generate a Personal Access Token in Calendly → Integrations → API & Webhooks.
            </div>
          </div>
        );
      case 'virustotal':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your VirusTotal API key" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              VirusTotal → Profile → API Key.
            </div>
          </div>
        );
      case 'abuseipdb':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your AbuseIPDB API key" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              abuseipdb.com → Account → API. Free tier allows ~1000 checks/day.
            </div>
          </div>
        );
      case 'shodan':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your Shodan API key" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              shodan.io → Account → My API Key.
            </div>
          </div>
        );
      case 'greynoise':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your GreyNoise API key" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              greynoise.io → My Account → API. Uses the community endpoint by default.
            </div>
          </div>
        );
      case 'splunk':
        return (
          <>
            <div>
              <label style={labelStyle}>Host URL</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="https://splunk.example.com:8089" required />
            </div>
            <div>
              <label style={labelStyle}>Auth Token (Bearer)</label>
              <input style={inputStyle} type="password" value={data.token || ''} onChange={e => handleFieldChange('token', e.target.value)} placeholder="Splunk auth token" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Splunk → Settings → Tokens. Used for /services/search/jobs.</div>
            </div>
            <div>
              <label style={labelStyle}>HEC URL (for index_event)</label>
              <input style={inputStyle} type="text" value={data.hec_url || ''} onChange={e => handleFieldChange('hec_url', e.target.value)} placeholder="https://splunk.example.com:8088/services/collector" />
            </div>
            <div>
              <label style={labelStyle}>HEC Token</label>
              <input style={inputStyle} type="password" value={data.hec_token || ''} onChange={e => handleFieldChange('hec_token', e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Leave blank to reuse the Auth Token above.</div>
            </div>
            <div>
              <label style={labelStyle}>
                <input type="checkbox" checked={data.verify_ssl !== false} onChange={e => handleFieldChange('verify_ssl', e.target.checked)} style={{ marginRight: 6 }} />
                Verify SSL certificate
              </label>
            </div>
          </>
        );
      case 'datadog_security':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Datadog API Key" required />
            </div>
            <div>
              <label style={labelStyle}>Application Key</label>
              <input style={inputStyle} type="password" value={data.application_key || ''} onChange={e => handleFieldChange('application_key', e.target.value)} placeholder="Datadog Application Key" required />
            </div>
            <div>
              <label style={labelStyle}>Site</label>
              <input style={inputStyle} type="text" value={data.site || ''} onChange={e => handleFieldChange('site', e.target.value)} placeholder="datadoghq.com (US1) / datadoghq.eu / us3.datadoghq.com / etc." />
            </div>
          </>
        );
      case 'sentinel':
        return (
          <>
            <div>
              <label style={labelStyle}>Tenant ID</label>
              <input style={inputStyle} type="text" value={data.tenant_id || ''} onChange={e => handleFieldChange('tenant_id', e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
            </div>
            <div>
              <label style={labelStyle}>Client ID (App Registration)</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Log Analytics Workspace ID</label>
              <input style={inputStyle} type="text" value={data.workspace_id || ''} onChange={e => handleFieldChange('workspace_id', e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Azure Portal → Log Analytics workspace → Overview → Workspace ID.</div>
            </div>
          </>
        );
      case 'crowdstrike':
        return (
          <>
            <div>
              <label style={labelStyle}>Client ID</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} placeholder="Falcon API Client ID" required />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Base URL</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="https://api.crowdstrike.com (US-1) / https://api.us-2.crowdstrike.com / https://api.eu-1.crowdstrike.com" />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Defaults to US-1. Set the regional endpoint that matches your tenant.</div>
            </div>
          </>
        );
      case 'okta':
        return (
          <>
            <div>
              <label style={labelStyle}>Okta Domain</label>
              <input style={inputStyle} type="text" value={data.domain || ''} onChange={e => handleFieldChange('domain', e.target.value)} placeholder="dev-12345.okta.com" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Just the host — no https:// prefix and no trailing slash.</div>
            </div>
            <div>
              <label style={labelStyle}>API Token</label>
              <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="00aBcDeFg..." required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Okta → Security → API → Tokens → Create Token.</div>
            </div>
          </>
        );
      case 'qradar':
        return (
          <>
            <div>
              <label style={labelStyle}>QRadar Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="https://qradar.example.com" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Include scheme (https://). No trailing slash.</div>
            </div>
            <div>
              <label style={labelStyle}>SEC Token</label>
              <input style={inputStyle} type="password" value={data.sec_token || ''} onChange={e => handleFieldChange('sec_token', e.target.value)} placeholder="Authorized service token" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>QRadar Admin → Authorized Services → Add. Sent as the SEC header.</div>
            </div>
            <div>
              <label style={labelStyle}>Verify SSL</label>
              <select style={inputStyle} value={data.verify_ssl === false ? 'false' : 'true'} onChange={e => handleFieldChange('verify_ssl', e.target.value === 'true')}>
                <option value="true">Yes (recommended)</option>
                <option value="false">No (self-signed certs)</option>
              </select>
            </div>
          </>
        );
      case 'microsoft_defender':
        return (
          <>
            <div>
              <label style={labelStyle}>Tenant ID</label>
              <input style={inputStyle} type="text" value={data.tenant_id || ''} onChange={e => handleFieldChange('tenant_id', e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
            </div>
            <div>
              <label style={labelStyle}>Client ID (Application ID)</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} placeholder="App registration client ID" required />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Entra ID → App registrations → your app → Certificates & secrets. Grant WindowsDefenderATP API permissions (Alert.ReadWrite.All, Machine.Isolate, etc.).</div>
            </div>
            <div>
              <label style={labelStyle}>Base URL</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="https://api.securitycenter.microsoft.com" />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Default is the WW endpoint. Use a sovereign cloud URL only if your tenant is on Defender US-Gov / UK / etc.</div>
            </div>
          </>
        );
      case 'microsoft_sentinel':
        return (
          <>
            <div>
              <label style={labelStyle}>Tenant ID</label>
              <input style={inputStyle} type="text" value={data.tenant_id || ''} onChange={e => handleFieldChange('tenant_id', e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
            </div>
            <div>
              <label style={labelStyle}>Client ID (Application ID)</label>
              <input style={inputStyle} type="text" value={data.client_id || ''} onChange={e => handleFieldChange('client_id', e.target.value)} placeholder="App registration client ID" required />
            </div>
            <div>
              <label style={labelStyle}>Client Secret</label>
              <input style={inputStyle} type="password" value={data.client_secret || ''} onChange={e => handleFieldChange('client_secret', e.target.value)} required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Grant Microsoft.SecurityInsights "Microsoft Sentinel Reader/Contributor" role on the workspace's subscription/RG.</div>
            </div>
            <div>
              <label style={labelStyle}>Subscription ID</label>
              <input style={inputStyle} type="text" value={data.subscription_id || ''} onChange={e => handleFieldChange('subscription_id', e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Azure subscription containing the Sentinel Log Analytics workspace. Resource group and workspace name go on the node, not here.</div>
            </div>
          </>
        );
      case 'tanium':
        return (
          <>
            <div>
              <label style={labelStyle}>Tanium Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="https://tanium.example.com" required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Include scheme (https://). No trailing slash.</div>
            </div>
            <div>
              <label style={labelStyle}>API Token (recommended)</label>
              <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="token-..." />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Tanium Console → Administration → Permissions → API Tokens. Sent as the `session` header. Leave blank to fall back to username + password.</div>
            </div>
            <div>
              <label style={labelStyle}>Username (fallback)</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} placeholder="soc.analyst" />
            </div>
            <div>
              <label style={labelStyle}>Password (fallback)</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Verify SSL</label>
              <select style={inputStyle} value={data.verify_ssl === false ? 'false' : 'true'} onChange={e => handleFieldChange('verify_ssl', e.target.value === 'true')}>
                <option value="true">Yes (recommended)</option>
                <option value="false">No (self-signed certs)</option>
              </select>
            </div>
          </>
        );
      case 'sumologic':
        return (
          <>
            <div>
              <label style={labelStyle}>Access ID</label>
              <input style={inputStyle} type="text" value={data.access_id || ''} onChange={e => handleFieldChange('access_id', e.target.value)} placeholder="su_..." required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Sumo Logic → Preferences → My Access Keys → + Add Access Key.</div>
            </div>
            <div>
              <label style={labelStyle}>Access Key</label>
              <input style={inputStyle} type="password" value={data.access_key || ''} onChange={e => handleFieldChange('access_key', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Deployment</label>
              <select style={inputStyle} value={data.deployment || 'us2'} onChange={e => handleFieldChange('deployment', e.target.value)}>
                <option value="us1">us1</option>
                <option value="us2">us2 (default)</option>
                <option value="au">au</option>
                <option value="ca">ca</option>
                <option value="de">de</option>
                <option value="eu">eu</option>
                <option value="fed">fed</option>
                <option value="in">in</option>
                <option value="jp">jp</option>
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>The Sumo Logic region your account lives in. Check service.sumologic.com vs service.us2.sumologic.com etc.</div>
            </div>
            <div>
              <label style={labelStyle}>Base URL (override)</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="https://api.us2.sumologic.com" />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Optional — only set if Sumo gave you a custom API endpoint.</div>
            </div>
          </>
        );
      case 'smtp':
        return (
          <>
            <div>
              <label style={labelStyle}>SMTP Server (Host)</label>
              <input style={inputStyle} type="text" value={data.smtp_server || ''} onChange={e => handleFieldChange('smtp_server', e.target.value)} placeholder="smtp.gmail.com" required />
            </div>
            <div>
              <label style={labelStyle}>SMTP Port</label>
              <input style={inputStyle} type="number" value={data.smtp_port || ''} onChange={e => handleFieldChange('smtp_port', e.target.value)} placeholder="587" required />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} placeholder="user@example.com" required />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Sender Email</label>
              <input style={inputStyle} type="email" value={data.sender || ''} onChange={e => handleFieldChange('sender', e.target.value)} placeholder="sender@example.com" required />
            </div>
          </>
        );
      case 'imap':
        return (
          <>
            <div>
              <label style={labelStyle}>IMAP Server (Host)</label>
              <input style={inputStyle} type="text" value={data.imap_host || ''} onChange={e => handleFieldChange('imap_host', e.target.value)} placeholder="imap.gmail.com" required />
            </div>
            <div>
              <label style={labelStyle}>IMAP Port</label>
              <input style={inputStyle} type="number" value={data.imap_port || ''} onChange={e => handleFieldChange('imap_port', e.target.value)} placeholder="993" required />
            </div>
            <div>
              <label style={labelStyle}>Email Address</label>
              <input style={inputStyle} type="email" value={data.email || ''} onChange={e => handleFieldChange('email', e.target.value)} placeholder="support@company.com" required />
            </div>
            <div>
              <label style={labelStyle}>Password / App Password</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} required />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>For Gmail, use an App Password. For Microsoft, use your account password or app password.</div>
            </div>
            <div>
              <label style={labelStyle}>
                <input type="checkbox" checked={data.use_ssl !== false} onChange={e => handleFieldChange('use_ssl', e.target.checked)} style={{ marginRight: 6 }} />
                Use SSL/TLS
              </label>
            </div>
          </>
        );
      case 'aws_s3':
      case 'aws_sqs':
      case 'aws_kinesis':
      case 'object_storage':
        return (
          <>
            <div>
              <label style={labelStyle}>AWS Access Key ID</label>
              <input style={inputStyle} type="text" value={data.aws_access_key_id || ''} onChange={e => handleFieldChange('aws_access_key_id', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>AWS Secret Access Key</label>
              <input style={inputStyle} type="password" value={data.aws_secret_access_key || ''} onChange={e => handleFieldChange('aws_secret_access_key', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>AWS Session Token (Optional)</label>
              <input style={inputStyle} type="password" value={data.aws_session_token || ''} onChange={e => handleFieldChange('aws_session_token', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Region</label>
              <input style={inputStyle} type="text" value={data.region || ''} onChange={e => handleFieldChange('region', e.target.value)} placeholder="us-east-1" />
            </div>
            {type === 'object_storage' && (
              <>
                <div>
                  <label style={labelStyle}>Endpoint URL (Optional)</label>
                  <input style={inputStyle} type="text" value={data.endpoint || ''} onChange={e => handleFieldChange('endpoint', e.target.value)} placeholder="https://s3.example.com" />
                </div>
                <div>
                  <label style={labelStyle}>Force Path Style</label>
                  <select style={inputStyle} value={String(Boolean(data.force_path_style))} onChange={e => handleFieldChange('force_path_style', e.target.value === 'true')}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </>
            )}
          </>
        );
      case 'azure_blob':
        return (
          <>
            <div>
              <label style={labelStyle}>Connection String (Optional)</label>
              <input style={inputStyle} type="password" value={data.connection_string || ''} onChange={e => handleFieldChange('connection_string', e.target.value)} placeholder="DefaultEndpointsProtocol=..." />
            </div>
            <div>
              <label style={labelStyle}>Account Name</label>
              <input style={inputStyle} type="text" value={data.account_name || ''} onChange={e => handleFieldChange('account_name', e.target.value)} placeholder="storageaccountname" />
            </div>
            <div>
              <label style={labelStyle}>Account Key</label>
              <input style={inputStyle} type="password" value={data.account_key || ''} onChange={e => handleFieldChange('account_key', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Account URL (Optional)</label>
              <input style={inputStyle} type="text" value={data.account_url || ''} onChange={e => handleFieldChange('account_url', e.target.value)} placeholder="https://storageaccountname.blob.core.windows.net" />
            </div>
          </>
        );
      case 'gcs':
      case 'google_cloud':
        return (
          <div>
            <label style={labelStyle}>Service Account JSON</label>
            <div style={{ marginBottom: '10px', fontSize: '0.9em', color: 'var(--text-tertiary)' }}>
              Upload your Google Cloud Service Account JSON file or paste its content below.
            </div>
            <ServiceAccountInput
              value={data}
              onChange={(newData) => setFormData(prev => ({ ...prev, data: newData }))}
              onError={(msg) => setError(msg)}
            />
          </div>
        );
      case 'anthropic':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="sk-ant-..." required />
          </div>
        );
      case 'ollama':
        return (
          <div>
            <label style={labelStyle}>Ollama Base URL</label>
            <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="http://localhost:11434" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              URL of your Ollama server. Use this credential when Ollama is on a remote host or behind a proxy. Leave blank for localhost — the node's Base URL field is used by default.
            </div>
          </div>
        );
      case 'github':
        return (
          <div>
            <label style={labelStyle}>Personal Access Token</label>
            <input style={inputStyle} type="password" value={data.token || ''} onChange={e => handleFieldChange('token', e.target.value)} placeholder="ghp_..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Requires scopes: repo, workflow (for Actions dispatch).
            </div>
          </div>
        );
      case 'google_calendar':
        return (
          <div>
            <label style={labelStyle}>Service Account JSON</label>
            <div style={{ marginBottom: '10px', fontSize: '0.9em', color: 'var(--text-tertiary)' }}>
              Upload your Google Service Account JSON file or paste its content. The service account must have Google Calendar API access and be shared with the target calendar.
            </div>
            <ServiceAccountInput
              value={data}
              onChange={(newData) => setFormData(prev => ({ ...prev, data: newData }))}
              onError={(msg) => setError(msg)}
            />
          </div>
        );
      case 'ssh':
        return (
          <>
            <div>
              <label style={labelStyle}>Host</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="192.168.1.100 or example.com" required />
            </div>
            <div>
              <label style={labelStyle}>Port</label>
              <input style={inputStyle} type="number" value={data.port || 22} onChange={e => handleFieldChange('port', e.target.value)} placeholder="22" />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} placeholder="ubuntu" required />
            </div>
            <div>
              <label style={labelStyle}>Password (optional if using private key)</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Private Key (PEM format, optional if using password)</label>
              <textarea style={{...inputStyle, fontFamily: 'monospace', minHeight: '100px'}} value={data.private_key || ''} onChange={e => handleFieldChange('private_key', e.target.value)} placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..." />
            </div>
          </>
        );
      case 'stripe':
        return (
          <div>
            <label style={labelStyle}>Secret Key</label>
            <input style={inputStyle} type="password" value={data.secret_key || ''} onChange={e => handleFieldChange('secret_key', e.target.value)} placeholder="sk_live_... or sk_test_..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Use a test key (sk_test_...) for development.
            </div>
          </div>
        );
      case 'quickbooks':
        return (
          <>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="eyJ..." required />
            </div>
            <div>
              <label style={labelStyle}>Realm ID (Company ID)</label>
              <input style={inputStyle} type="text" value={data.realm_id || ''} onChange={e => handleFieldChange('realm_id', e.target.value)} placeholder="123456789012345" required />
            </div>
          </>
        );
      case 'xero':
        return (
          <>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="eyJ..." required />
            </div>
            <div>
              <label style={labelStyle}>Tenant ID</label>
              <input style={inputStyle} type="text" value={data.tenant_id || ''} onChange={e => handleFieldChange('tenant_id', e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
            </div>
          </>
        );
      case 'exchange_rate':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key (optional)</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Optional key for your FX provider" />
            </div>
            <div>
              <label style={labelStyle}>Base URL (optional)</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="https://api.exchangerate.host" />
            </div>
          </>
        );
      case 'url_shortener':
        return (
          <>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="Bitly access token" required />
            </div>
            <div>
              <label style={labelStyle}>Base URL (optional)</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="https://api-ssl.bitly.com/v4" />
            </div>
          </>
        );
      case 'salesforce':
        return (
          <>
            <div>
              <label style={labelStyle}>Instance URL</label>
              <input style={inputStyle} type="text" value={data.instance_url || ''} onChange={e => handleFieldChange('instance_url', e.target.value)} placeholder="https://mycompany.salesforce.com" required />
            </div>
            <div>
              <label style={labelStyle}>Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="00D..." required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Obtain via OAuth 2.0 or the Salesforce CLI (<code>sf org display</code>).
              </div>
            </div>
          </>
        );
      case 'twilio':
        return (
          <>
            <div>
              <label style={labelStyle}>Account SID</label>
              <input style={inputStyle} type="text" value={data.account_sid || ''} onChange={e => handleFieldChange('account_sid', e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" required />
            </div>
            <div>
              <label style={labelStyle}>Auth Token</label>
              <input style={inputStyle} type="password" value={data.auth_token || ''} onChange={e => handleFieldChange('auth_token', e.target.value)} placeholder="Your Auth Token" required />
            </div>
          </>
        );
      case 'sendgrid':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="SG...." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Create an API key in SendGrid Settings → API Keys with at least Mail Send permission.
            </div>
          </div>
        );
      case 'mailchimp':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="abc123abc123abc123abc123abc123ab-us1" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Found in Mailchimp Account → Extras → API keys. The key ends with your datacenter (e.g. <code>-us1</code>).
            </div>
          </div>
        );
      case 'twitter':
        return (
          <>
            <div>
              <label style={labelStyle}>Bearer Token</label>
              <input style={inputStyle} type="password" value={data.bearer_token || ''} onChange={e => handleFieldChange('bearer_token', e.target.value)} placeholder="AAAA..." required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Required for read operations (search, get user, timeline). Found in your Twitter Developer Portal app.
              </div>
            </div>
            <div>
              <label style={labelStyle}>Access Token (for write operations)</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="Optional — needed for post_tweet and send_dm" />
            </div>
          </>
        );
      case 'linkedin':
        return (
          <div>
            <label style={labelStyle}>OAuth 2.0 Access Token</label>
            <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="AQV..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Generate a user access token via the LinkedIn Developer portal. Required scopes: r_liteprofile, r_emailaddress, w_member_social.
            </div>
          </div>
        );
      case 'instagram':
        return (
          <>
            <div>
              <label style={labelStyle}>Page Access Token</label>
              <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="EAAGm..." required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                A long-lived Facebook Page Access Token with instagram_basic, instagram_content_publish, and pages_read_engagement permissions.
              </div>
            </div>
            <div>
              <label style={labelStyle}>Instagram Business Account ID</label>
              <input style={inputStyle} type="text" value={data.ig_user_id || ''} onChange={e => handleFieldChange('ig_user_id', e.target.value)} placeholder="17841400000000000" required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                The numeric ID of your Instagram Business/Creator account (not your Facebook Page ID).
              </div>
            </div>
          </>
        );
      case 'convertkit':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="abc123..." required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Found in ConvertKit → Settings → Advanced → API Key.
              </div>
            </div>
            <div>
              <label style={labelStyle}>API Secret</label>
              <input style={inputStyle} type="password" value={data.api_secret || ''} onChange={e => handleFieldChange('api_secret', e.target.value)} placeholder="def456... — required for creating broadcasts" />
            </div>
          </>
        );
      case 'zendesk':
        return (
          <>
            <div>
              <label style={labelStyle}>Subdomain</label>
              <input style={inputStyle} type="text" value={data.subdomain || ''} onChange={e => handleFieldChange('subdomain', e.target.value)} placeholder="mycompany" required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                The part before <code>.zendesk.com</code> in your Zendesk URL.
              </div>
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={data.email || ''} onChange={e => handleFieldChange('email', e.target.value)} placeholder="agent@example.com" required />
            </div>
            <div>
              <label style={labelStyle}>API Token</label>
              <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="Your Zendesk API token" required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Found in Admin → Channels → API → Token Access.
              </div>
            </div>
          </>
        );
      case 'monday':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="Your Monday.com API v2 key" required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Found in Monday.com → Profile → Developers → My Access Tokens.
              </div>
            </div>
          </>
        );
      case 'linear':
        return (
          <>
            <div>
              <label style={labelStyle}>API Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="lin_api_..." required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Found in Linear → Settings → API → Personal API Keys.
              </div>
            </div>
          </>
        );
      case 'asana':
        return (
          <div>
            <label style={labelStyle}>Personal Access Token</label>
            <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="1/123456789:abcdef..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Found in Asana → Profile → My Profile Settings → Apps → Manage Developer Apps → Create New Token.
            </div>
          </div>
        );
      case 'clickup':
        return (
          <div>
            <label style={labelStyle}>Personal API Token</label>
            <input style={inputStyle} type="password" value={data.api_token || ''} onChange={e => handleFieldChange('api_token', e.target.value)} placeholder="pk_..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Found in ClickUp → Settings → Apps → API Token.
            </div>
          </div>
        );
      case 'shopify':
        return (
          <div>
            <label style={labelStyle}>Shop Subdomain</label>
            <input style={inputStyle} type="text" value={data.shop || ''} onChange={e => handleFieldChange('shop', e.target.value)} placeholder="my-store (without .myshopify.com)" required />
            <label style={labelStyle}>Access Token</label>
            <input style={inputStyle} type="password" value={data.access_token || ''} onChange={e => handleFieldChange('access_token', e.target.value)} placeholder="shpat_..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Found in Shopify Admin → Settings → Apps and sales channels → Develop apps → Your App → API credentials.
            </div>
          </div>
        );
      case 'woocommerce':
        return (
          <div>
            <label style={labelStyle}>Site URL</label>
            <input style={inputStyle} type="text" value={data.site_url || ''} onChange={e => handleFieldChange('site_url', e.target.value)} placeholder="https://mystore.com" required />
            <label style={labelStyle}>Consumer Key</label>
            <input style={inputStyle} type="text" value={data.consumer_key || ''} onChange={e => handleFieldChange('consumer_key', e.target.value)} placeholder="ck_..." required />
            <label style={labelStyle}>Consumer Secret</label>
            <input style={inputStyle} type="password" value={data.consumer_secret || ''} onChange={e => handleFieldChange('consumer_secret', e.target.value)} placeholder="cs_..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Found in WooCommerce → Settings → Advanced → REST API → Add key.
            </div>
          </div>
        );
      case 'firebase':
        return (
          <>
            <div>
              <label style={labelStyle}>Firebase Project ID</label>
              <input style={inputStyle} type="text" value={data.project_id || ''} onChange={e => handleFieldChange('project_id', e.target.value)} placeholder="my-project-12345" required />
            </div>
            <div>
              <label style={labelStyle}>API Key (Web API Key)</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="AIzaSy..." />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>Firebase Console → Project Settings → Web API Key.</div>
            </div>
            <div>
              <label style={labelStyle}>ID Token (optional, for Auth-protected collections)</label>
              <input style={inputStyle} type="password" value={data.id_token || ''} onChange={e => handleFieldChange('id_token', e.target.value)} />
            </div>
          </>
        );
      case 'dynamodb':
        return (
          <>
            <div>
              <label style={labelStyle}>AWS Access Key ID</label>
              <input style={inputStyle} type="text" value={data.access_key_id || ''} onChange={e => handleFieldChange('access_key_id', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>AWS Secret Access Key</label>
              <input style={inputStyle} type="password" value={data.secret_access_key || ''} onChange={e => handleFieldChange('secret_access_key', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Region</label>
              <input style={inputStyle} type="text" value={data.region || ''} onChange={e => handleFieldChange('region', e.target.value)} placeholder="us-east-1" required />
            </div>
            <div>
              <label style={labelStyle}>Endpoint URL (optional, for local DynamoDB)</label>
              <input style={inputStyle} type="text" value={data.endpoint_url || ''} onChange={e => handleFieldChange('endpoint_url', e.target.value)} placeholder="http://localhost:8000" />
            </div>
          </>
        );
      case 'supabase':
        return (
          <>
            <div>
              <label style={labelStyle}>Project URL</label>
              <input style={inputStyle} type="text" value={data.project_url || ''} onChange={e => handleFieldChange('project_url', e.target.value)} placeholder="https://xxxx.supabase.co" required />
            </div>
            <div>
              <label style={labelStyle}>Service Role Key (for full access)</label>
              <input style={inputStyle} type="password" value={data.service_role_key || ''} onChange={e => handleFieldChange('service_role_key', e.target.value)} placeholder="eyJhbGci..." />
            </div>
            <div>
              <label style={labelStyle}>Anon / Public Key</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="eyJhbGci..." />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>Use service_role key for backend workflows to bypass RLS.</div>
            </div>
          </>
        );
      case 'airtable':
        return (
          <div>
            <label style={labelStyle}>Personal Access Token</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="patXXXXXXXXXXXXXX" required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Airtable → Account → Developer Hub → Personal access tokens. Requires data.records:read/write scope.
            </div>
          </div>
        );
      case 'elasticsearch':
        return (
          <>
            <div>
              <label style={labelStyle}>Host URL</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="http://localhost:9200 or https://xxx.es.io:9243" required />
            </div>
            <div>
              <label style={labelStyle}>API Key (preferred for Elastic Cloud)</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Username (basic auth)</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} placeholder="elastic" />
            </div>
            <div>
              <label style={labelStyle}>Password (basic auth)</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} />
            </div>
          </>
        );
      case 'neon':
        return (
          <>
            <div>
              <label style={labelStyle}>Host / Endpoint</label>
              <input style={inputStyle} type="text" value={data.host || ''} onChange={e => handleFieldChange('host', e.target.value)} placeholder="ep-xxx.us-east-2.aws.neon.tech (Neon) or aws.connect.psdb.cloud (PlanetScale)" required />
            </div>
            <div>
              <label style={labelStyle}>API Key / Auth Token</label>
              <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Username (PlanetScale / Turso)</label>
              <input style={inputStyle} type="text" value={data.username || ''} onChange={e => handleFieldChange('username', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Password (PlanetScale)</label>
              <input style={inputStyle} type="password" value={data.password || ''} onChange={e => handleFieldChange('password', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Database URL (Turso)</label>
              <input style={inputStyle} type="text" value={data.db_url || ''} onChange={e => handleFieldChange('db_url', e.target.value)} placeholder="https://xxx.turso.io" />
            </div>
          </>
        );
      case 'perplexity':
        return (
          <div>
            <label style={labelStyle}>API Key</label>
            <input style={inputStyle} type="password" value={data.api_key || ''} onChange={e => handleFieldChange('api_key', e.target.value)} placeholder="pplx-... or tvly-..." required />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
              Perplexity: api.perplexity.ai/settings/api. Tavily: app.tavily.com.
            </div>
          </div>
        );
      case 'gitlab':
        return (
          <>
            <div>
              <label style={labelStyle}>Personal Access Token</label>
              <input style={inputStyle} type="password" value={data.token || ''} onChange={e => handleFieldChange('token', e.target.value)} placeholder="glpat-..." required />
            </div>
            <div>
              <label style={labelStyle}>GitLab Base URL (leave blank for gitlab.com)</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="https://gitlab.example.com" />
            </div>
          </>
        );
      case 'docker':
        return (
          <>
            <div>
              <label style={labelStyle}>Docker Host URL</label>
              <input style={inputStyle} type="text" value={data.base_url || ''} onChange={e => handleFieldChange('base_url', e.target.value)} placeholder="http://localhost:2375 or http://10.0.0.1:2375" required />
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '10px' }}>
                Ensure the Docker daemon is exposed via TCP. Use with caution — no TLS = unauthenticated access.
              </div>
            </div>
          </>
        );
      case 'kubernetes':
        return (
          <>
            <div>
              <label style={labelStyle}>API Server URL</label>
              <input style={inputStyle} type="text" value={data.server || ''} onChange={e => handleFieldChange('server', e.target.value)} placeholder="https://your-cluster-api:6443" required />
            </div>
            <div>
              <label style={labelStyle}>Service Account Token</label>
              <input style={inputStyle} type="password" value={data.token || ''} onChange={e => handleFieldChange('token', e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>CA Certificate (Base64, optional)</label>
              <textarea style={{...inputStyle, fontFamily: 'monospace', minHeight: '60px'}} value={data.ca_cert || ''} onChange={e => handleFieldChange('ca_cert', e.target.value)} placeholder="LS0tLS1..." />
            </div>
            <div>
              <label style={labelStyle}>Skip TLS Verify</label>
              <select style={inputStyle} value={data.skip_tls_verify ? 'true' : 'false'} onChange={e => handleFieldChange('skip_tls_verify', e.target.value === 'true')}>
                <option value="false">No (recommended)</option>
                <option value="true">Yes (insecure)</option>
              </select>
            </div>
          </>
        );
      default:
        return <div>Unknown service type</div>;
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '24px', cursor: 'pointer', marginRight: '15px' }}>
            ←
          </button>
        )}
        <h1 style={{ margin: 0, fontSize: '24px' }}>Credential Manager</h1>
      </div>

      <div style={contentStyle}>
        {error && (
          <div style={{ padding: '15px', backgroundColor: 'var(--error-color)', color: 'var(--text-inverse)', marginBottom: '20px', borderRadius: '4px' }}>
            {error}
            <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--text-inverse)', cursor: 'pointer', fontSize: '16px' }}>&times;</button>
          </div>
        )}

        {!isAdding ? (
          <>
            {/* Toolbar: Search + Add button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search credentials..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  style={{ ...inputStyle, marginBottom: 0, paddingLeft: '32px' }}
                />
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: '14px', pointerEvents: 'none' }}>&#128269;</span>
              </div>
              <button style={{ ...buttonStyle, marginRight: 0, whiteSpace: 'nowrap' }} onClick={() => {
                setCurrentCredentialId(null);
                setEditingOAuthManaged(false);
                setFormData({ name: '', service_type: 'postgres', data: {} });
                setIsAdding(true);
              }}>+ Add New</button>
            </div>

            {(() => {
              const filtered = credentials.filter(cred => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                const typeLabel = serviceTypes.find(t => t.value === cred.service_type)?.label || cred.service_type;
                return cred.name.toLowerCase().includes(q) || typeLabel.toLowerCase().includes(q) || cred.service_type.toLowerCase().includes(q);
              });
              const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
              const safePage = Math.min(currentPage, totalPages);
              const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

              return (
                <>
                  <div style={{ marginBottom: '12px', overflowY: 'auto', flex: 1 }}>
                    {filtered.length === 0 ? (
                      <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '40px', border: '2px dashed var(--border-color)', borderRadius: '8px' }}>
                        {credentials.length === 0
                          ? 'No credentials found. Click "+ Add New" to get started.'
                          : `No credentials matching "${searchQuery}"`}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                            <th style={{ padding: '12px', width: '40px' }}>
                              <input
                                type="checkbox"
                                checked={paged.length > 0 && paged.every(credential => selectedCredentialIds.includes(credential.id))}
                                onChange={() => {
                                  const pageIds = paged.map(c => c.id);
                                  const allSelected = pageIds.every(id => selectedCredentialIds.includes(id));
                                  if (allSelected) {
                                    setSelectedCredentialIds(prev => prev.filter(id => !pageIds.includes(id)));
                                  } else {
                                    setSelectedCredentialIds(prev => [...new Set([...prev, ...pageIds])]);
                                  }
                                }}
                              />
                            </th>
                            <th style={{ padding: '12px' }}>Name</th>
                            <th style={{ padding: '12px' }}>Type</th>
                            <th style={{ padding: '12px' }}>Status</th>
                            <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paged.map(cred => {
                            let healthStatus = null;
                            let healthLabel = '';
                            if (cred.oauth_managed) {
                              if (!cred.token_expires_at) {
                                healthStatus = 'valid'; healthLabel = 'Connected';
                              } else {
                                const expiresAt = new Date(cred.token_expires_at);
                                const now = new Date();
                                const hoursLeft = (expiresAt - now) / (1000 * 60 * 60);
                                if (hoursLeft <= 0) { healthStatus = 'expired'; healthLabel = 'Expired'; }
                                else if (hoursLeft <= 24) { healthStatus = 'expiring'; healthLabel = `Expires in ${Math.round(hoursLeft)}h`; }
                                else { healthStatus = 'valid'; healthLabel = 'Connected'; }
                              }
                            }
                            const healthColor = healthStatus === 'valid' ? '#22c55e' : healthStatus === 'expiring' ? '#eab308' : healthStatus === 'expired' ? '#ef4444' : null;
                            const oauthProvider = SERVICE_TYPE_OAUTH_MAP[cred.service_type];
                            return (
                            <tr key={cred.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={{ padding: '12px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedCredentialIds.includes(cred.id)}
                                  onChange={() => toggleCredentialSelection(cred.id)}
                                />
                              </td>
                              <td style={{ padding: '12px' }}>{cred.name}</td>
                              <td style={{ padding: '12px' }}>{serviceTypes.find(t => t.value === cred.service_type)?.label || cred.service_type}</td>
                              <td style={{ padding: '12px' }}>
                                {healthColor ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: healthColor, display: 'inline-block' }} />
                                    {healthLabel}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>Manual</span>
                                )}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'right', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                {healthStatus === 'expired' && oauthProvider && (
                                  <button
                                    style={{ ...buttonStyle, padding: '6px 12px', fontSize: '12px', background: '#d97706', borderColor: '#d97706' }}
                                    onClick={() => handleOAuthConnect(oauthProvider, cred.service_type, cred.id)}
                                  >
                                    Reconnect
                                  </button>
                                )}
                                <button
                                  style={{ ...buttonStyle, padding: '6px 12px', fontSize: '12px' }}
                                  onClick={() => handleEdit(cred.id)}
                                >
                                  Edit
                                </button>
                                <button
                                  style={{ ...deleteButtonStyle, padding: '6px 12px', fontSize: '12px' }}
                                  onClick={() => handleDelete(cred.id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Bottom bar: selection actions + pagination */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      <span>{selectedCredentialIds.length} selected</span>
                      <button
                        style={{ ...deleteButtonStyle, padding: '6px 12px', fontSize: '12px', opacity: selectedCredentialIds.length === 0 ? 0.6 : 1, cursor: selectedCredentialIds.length === 0 ? 'not-allowed' : 'pointer' }}
                        disabled={selectedCredentialIds.length === 0}
                        onClick={handleBulkDelete}
                      >
                        Delete Selected
                      </button>
                    </div>
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={safePage <= 1}
                          style={{ ...secondaryButtonStyle, padding: '4px 10px', fontSize: '12px', marginRight: 0, opacity: safePage <= 1 ? 0.5 : 1, cursor: safePage <= 1 ? 'not-allowed' : 'pointer' }}
                        >
                          Prev
                        </button>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {safePage} / {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={safePage >= totalPages}
                          style={{ ...secondaryButtonStyle, padding: '4px 10px', fontSize: '12px', marginRight: 0, opacity: safePage >= totalPages ? 0.5 : 1, cursor: safePage >= totalPages ? 'not-allowed' : 'pointer' }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Name</label>
              <input
                style={inputStyle}
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="My Credentials"
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Service Type</label>
              <div ref={serviceTypeDropdownRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => { setServiceTypeOpen(o => !o); setServiceTypeSearch(''); }}
                  style={{
                    ...inputStyle,
                    marginBottom: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{serviceTypes.find(t => t.value === formData.service_type)?.label || formData.service_type}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '11px', marginLeft: '8px' }}>▼</span>
                </button>
                {serviceTypeOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    boxShadow: '0 4px 16px var(--shadow-color)',
                    marginTop: '2px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ padding: '8px 8px 4px' }}>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search service types..."
                        value={serviceTypeSearch}
                        onChange={e => setServiceTypeSearch(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit',
                          fontSize: '13px',
                          background: 'var(--bg-input)',
                          color: 'var(--text-primary)',
                          outline: 'none'
                        }}
                      />
                    </div>
                    <div style={{ overflowY: 'auto', maxHeight: '240px' }}>
                      {serviceTypes
                        .filter(t =>
                          t.label.toLowerCase().includes(serviceTypeSearch.toLowerCase()) ||
                          t.value.toLowerCase().includes(serviceTypeSearch.toLowerCase())
                        )
                        .map(t => (
                          <div
                            key={t.value}
                            onMouseDown={() => {
                              handleTypeChange({ target: { value: t.value } });
                              setServiceTypeOpen(false);
                              setServiceTypeSearch('');
                            }}
                            style={{
                              padding: '9px 12px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              background: t.value === formData.service_type ? 'rgba(99,102,241,0.1)' : 'transparent',
                              color: t.value === formData.service_type ? 'var(--accent-color)' : 'var(--text-primary)',
                              borderLeft: t.value === formData.service_type ? '2px solid var(--accent-color)' : '2px solid transparent'
                            }}
                            onMouseEnter={e => { if (t.value !== formData.service_type) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                            onMouseLeave={e => { if (t.value !== formData.service_type) e.currentTarget.style.background = 'transparent'; }}
                          >
                            {t.label}
                          </div>
                        ))
                      }
                      {serviceTypes.filter(t =>
                        t.label.toLowerCase().includes(serviceTypeSearch.toLowerCase()) ||
                        t.value.toLowerCase().includes(serviceTypeSearch.toLowerCase())
                      ).length === 0 && (
                        <div style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', fontSize: '13px' }}>
                          No matching service type
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* OAuth one-click connect button — or setup guidance when no app is configured */}
            {(() => {
              const provider = SERVICE_TYPE_OAUTH_MAP[formData.service_type];
              if (!provider || currentCredentialId) return null; // not an OAuth service or editing existing

              const oauthEntry = oauthProviders.find(p => p.provider === provider);
              const providerMeta = allOauthProviders.find(p => p.provider === provider);
              const providerLabel = providerMeta?.label || provider.charAt(0).toUpperCase() + provider.slice(1);

              // Case 1: OAuth is available — show connect button
              if (oauthEntry) {
                return (
                  <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--accent-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                        Connect with {oauthEntry.label}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>
                        Click to authorize via OAuth — no manual token copy needed.
                        {oauthEntry.is_bundled && (
                          <span style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>
                            Powered by xavvy
                          </span>
                        )}
                      </div>
                      {oauthEntry.redirect_uri && !oauthEntry.is_bundled && (
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                          Register this redirect URI in your {oauthEntry.label} OAuth app:&nbsp;
                          <code
                            title="Click to copy"
                            onClick={() => navigator.clipboard?.writeText(oauthEntry.redirect_uri)}
                            style={{ background: 'var(--bg-primary)', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', userSelect: 'all', fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)' }}
                          >
                            {oauthEntry.redirect_uri}
                          </code>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={oauthConnecting}
                      onClick={() => handleOAuthConnect(provider, formData.service_type)}
                      style={{ ...buttonStyle, marginRight: 0, minWidth: '140px', opacity: oauthConnecting ? 0.7 : 1 }}
                    >
                      {oauthConnecting ? 'Connecting...' : `Connect with ${oauthEntry.label}`}
                    </button>
                  </div>
                );
              }

              // Case 2: OAuth NOT available — show user-facing setup guide for manual credentials
              const setupGuides = {
                google: {
                  title: 'Google Account',
                  steps: [
                    'Go to Google Cloud Console → APIs & Services → Credentials',
                    'Click "Create Credentials" → "OAuth client ID" (or use a Service Account JSON key)',
                    'Copy the Client ID and Client Secret, or download the Service Account JSON file',
                    'Paste them in the form below',
                  ],
                  hint: 'You\'ll also need to enable the APIs you want to use (Sheets, Gmail, Drive, Calendar) under APIs & Services → Library.',
                },
                microsoft: {
                  title: 'Microsoft 365',
                  steps: [
                    'Go to Azure Portal → App Registrations → New Registration',
                    'Select "Accounts in any organizational directory" and register',
                    'Go to Certificates & Secrets → New Client Secret and copy the Value',
                    'Copy the Application (Client) ID from the Overview page and your Directory (Tenant) ID',
                    'Paste Client ID, Client Secret, and Tenant ID in the form below',
                  ],
                  hint: 'Add API permissions for Mail.Send, Files.ReadWrite, and User.Read under API Permissions.',
                },
                slack: {
                  title: 'Slack',
                  steps: [
                    'Go to api.slack.com/apps → Create New App → From Scratch',
                    'Under OAuth & Permissions, add scopes: channels:read, chat:write, files:write, users:read',
                    'Install to your workspace, then copy the Bot User OAuth Token',
                    'Paste it in the form below',
                  ],
                  hint: null,
                },
                github: {
                  title: 'GitHub',
                  steps: [
                    'Go to GitHub → Settings → Developer Settings → Personal Access Tokens → Fine-grained tokens',
                    'Click "Generate new token", select the repositories and permissions you need',
                    'Copy the token and paste it in the form below',
                  ],
                  hint: 'For classic tokens: Settings → Developer Settings → Personal Access Tokens → Tokens (classic).',
                },
                notion: {
                  title: 'Notion',
                  steps: [
                    'Go to notion.so/my-integrations → New Integration',
                    'Give it a name, select the workspace, and submit',
                    'Copy the Internal Integration Secret',
                    'Paste it in the form below',
                  ],
                  hint: 'Share the pages/databases you want to access with this integration inside Notion.',
                },
                hubspot: {
                  title: 'HubSpot',
                  steps: [
                    'Go to HubSpot → Settings → Integrations → Private Apps → Create a Private App',
                    'Set the required scopes (CRM contacts, deals, etc.)',
                    'Copy the Access Token after creation',
                    'Paste it in the form below',
                  ],
                  hint: null,
                },
                linear: {
                  title: 'Linear',
                  steps: [
                    'Go to Linear → Settings → API → Personal API Keys',
                    'Create a new key and copy it',
                    'Paste it in the form below',
                  ],
                  hint: null,
                },
              };
              const guide = setupGuides[provider];
              if (!guide) return null;

              return (
                <div style={{
                  marginBottom: '20px', padding: '16px', background: 'var(--bg-secondary)',
                  borderRadius: '8px', border: '1px dashed var(--border-color)',
                }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                    How to get your {guide.title} credentials
                  </div>
                  <ol style={{ margin: '0 0 0 16px', padding: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                    {guide.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                  {guide.hint && (
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '8px', fontStyle: 'italic' }}>
                      {guide.hint}
                    </div>
                  )}
                </div>
              );
            })()}

            {editingOAuthManaged && currentCredentialId ? (
              <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                  OAuth Connection
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                  <span style={{ color: 'var(--text-primary)' }}>Connected via OAuth</span>
                </div>
                <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', margin: '0 0 16px 0' }}>
                  This credential was created via OAuth and is managed automatically. To update the token, use the Reconnect button below.
                </p>
                {(() => {
                  const provider = SERVICE_TYPE_OAUTH_MAP[formData.service_type];
                  const oauthEntry = provider && oauthProviders.find(p => p.provider === provider);
                  if (oauthEntry) {
                    return (
                      <button
                        type="button"
                        disabled={oauthConnecting}
                        onClick={() => handleOAuthConnect(provider, formData.service_type, currentCredentialId)}
                        style={{ ...buttonStyle, background: '#d97706', borderColor: '#d97706' }}
                      >
                        {oauthConnecting ? 'Reconnecting...' : 'Reconnect'}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : (
              <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                  Manual Configuration
                  {SERVICE_TYPE_OAUTH_MAP[formData.service_type] && oauthProviders.find(p => p.provider === SERVICE_TYPE_OAUTH_MAP[formData.service_type]) && !currentCredentialId && (
                    <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--text-tertiary)' }}>(or use the button above)</span>
                  )}
                </h3>
                {renderFields()}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center' }}>
              {testMessage && (
                <span style={{ 
                  marginRight: 'auto', 
                  padding: '5px 10px', 
                  borderRadius: '4px', 
                  backgroundColor: testStatus === 'success' ? 'rgba(40, 167, 69, 0.2)' : (testStatus === 'error' ? 'rgba(220, 53, 69, 0.2)' : 'transparent'),
                  color: testStatus === 'success' ? 'var(--success-color)' : (testStatus === 'error' ? 'var(--error-color)' : 'var(--text-secondary)'),
                  fontSize: '14px'
                }}>
                  {testMessage}
                </span>
              )}
              <button 
                type="button" 
                style={{ ...secondaryButtonStyle, backgroundColor: 'var(--success-color)', color: 'var(--text-inverse)', border: 'none' }} 
                onClick={handleTestConnection} 
                disabled={testStatus === 'testing'}
              >
                {testStatus === 'testing' ? 'Testing...' : 'Test Connection'}
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => { setIsAdding(false); setEditingOAuthManaged(false); setTestStatus('idle'); setTestMessage(''); }}>Cancel</button>
              <button type="submit" style={buttonStyle}>Save Credential</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Helper component for JSON input with File Upload
function ServiceAccountInput({ value, onChange, onError }) {
  const [text, setText] = useState(JSON.stringify(value, null, 2) === '{}' ? '' : JSON.stringify(value, null, 2));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    // Reset text if value becomes empty (e.g. type switch) and we have content
    // This handles the case where parent resets data
    if (JSON.stringify(value) === '{}' && text !== '') {
        // Only if we are not currently typing? 
        // Actually, if parent resets, we should reset.
        // But be careful not to reset while typing valid json that matches value.
        // For now, trust the parent's reset only on mount or explicit clear.
    }
  }, [value]);

  const validateAndChange = (newText) => {
    setText(newText);
    try {
      const parsed = JSON.parse(newText);
      onChange(parsed);
      setLocalError(null);
      if (onError) onError(null);
    } catch (err) {
      setLocalError("Invalid JSON");
      if (onError) onError("Invalid JSON in Service Account field");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Clear input immediately
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      validateAndChange(content);
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <input 
        type="file" 
        accept=".json" 
        onChange={handleFileChange}
        style={{ marginBottom: '10px', color: 'var(--text-secondary)' }}
      />
      <JsonEditor
        style={{ 
            marginBottom: '10px'
        }}
        value={text}
        onChange={(val) => validateAndChange(val)}
        showAssistTools={false}
        placeholder='{ "type": "service_account", ... }'
      />
      {localError && <div style={{ color: 'var(--error-color)', fontSize: '12px' }}>{localError}</div>}
    </div>
  );
}
